import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Sparkles, CheckCircle2,
  Stethoscope, HeartHandshake, Award, MapPin,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingAppPanel from '../components/FloatingAppPanel';
import HowVietClinicsWorks from '../components/HowVietClinicsWorks';
import PopularTreatments from '../components/PopularTreatments';
import UrgentCareCitiesSection from '../components/UrgentCareCitiesSection';
import CategoryDestinationsSection from '../components/CategoryDestinationsSection';
import TreatmentGuides from '../components/TreatmentGuides';
import RequestAppointmentForm from '../components/RequestAppointmentForm';
import { useLanguage } from '../context/LanguageContext';
import { useCatalog } from '../context/CatalogContext';
import { getHomeHeroImage } from '../lib/imageOverrides';
import { buildLocalizedPath } from '../components/LocaleRouter';
import { PUBLIC_PAGE_SEO } from '../data/publicPageSeo';

// Default hero asset — overridable from the admin panel via
// /api/v1/settings/web-content-overrides → home.heroImageUrl
const HERO_DEFAULT = 'https://customer-assets.emergentagent.com/job_5a81bd9f-a300-47ec-b521-267995465c4c/artifacts/c0pmwk0c_z7839008540484_82377df49358e26afcea9fd3bfe091fe.jpg';
const HERO_FALLBACK = 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';

// Phone mockup slot — defaults to the supplied pre-composed PNG (iPhone frame
// + screen baked in). Overridable from the admin panel via
// /api/v1/settings/web-content-overrides → home.appMockupUrl
const PHONE_MOCKUP_DEFAULT = 'https://customer-assets.emergentagent.com/job_5a81bd9f-a300-47ec-b521-267995465c4c/artifacts/h0cpy1fw_WhatsApp%20Image%202026-05-22%20at%2014.15.35.png';

const TRUST_FEATURES = [
  { icon: Stethoscope,    title: 'Provider Options',        sub: 'Subject to provider review' },
  { icon: HeartHandshake, title: 'Patient Support',         sub: 'Help coordinating next steps' },
  { icon: Award,          title: 'Independent Coordination', sub: 'Clinical decisions stay with providers' },
  { icon: MapPin,         title: 'Care in Vietnam',         sub: 'Availability checked for your location' },
];

const COORDINATION_BULLETS = [
  'Share your needs and preferred location',
  'Our team reviews your enquiry',
  'Providers confirm options and pricing',
  'We help coordinate agreed next steps',
];

const Home = () => {
  const { overrides } = useCatalog();
  const heroImageUrl = getHomeHeroImage(overrides, HERO_DEFAULT);
  const phoneMockupUrl = overrides?.home?.appMockupUrl || PHONE_MOCKUP_DEFAULT;
  const { t, language } = useLanguage();
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{PUBLIC_PAGE_SEO.home.title}</title>
        <meta name="description" content={PUBLIC_PAGE_SEO.home.description} />
        <meta property="og:title" content={PUBLIC_PAGE_SEO.home.title} />
        <meta property="og:description" content={PUBLIC_PAGE_SEO.home.description} />
        <link rel="canonical" href="https://vietclinics.com/" />
      </Helmet>
      <Header />

      {/* ── 1. HERO — Premium concierge healthcare experience ─────────────── */}
      <section
        className="relative overflow-hidden bg-white"
        data-testid="home-hero-section"
      >
        {/* Cinematic background photo — single <img> so we can apply a subtle
            warmth/contrast filter (CSS `filter` only works on real elements,
            not on a `background-image`). Anchored to the right so the advisor
            sits in the right two-thirds across all breakpoints. */}
        <img
          src={heroImageUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: 'right center',
            // Subtle warmth + clarity — preserves natural skin tones.
            filter: 'saturate(1.10) contrast(1.06) brightness(1.03)',
          }}
          onError={(e) => { e.target.src = HERO_FALLBACK; }}
        />

        {/* Left-side readability gradient — strong over the copy, almost gone
            past the centre so the advisor's face stays crisp on the right. */}
        <div
          aria-hidden
          className="hidden lg:block absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg,'
              + ' rgba(240,250,248,0.96) 0%,'
              + ' rgba(240,250,248,0.88) 28%,'
              + ' rgba(240,250,248,0.45) 44%,'
              + ' rgba(240,250,248,0.10) 58%,'
              + ' rgba(255,255,255,0) 72%)',
          }}
        />
        {/* Mobile overlay — vertical fade keeps the photo lifestyle while
            ensuring copy stays readable on small screens. */}
        <div
          aria-hidden
          className="lg:hidden absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg,'
              + ' rgba(240,250,248,0.92) 0%,'
              + ' rgba(240,250,248,0.86) 30%,'
              + ' rgba(240,250,248,0.55) 55%,'
              + ' rgba(240,250,248,0.20) 80%,'
              + ' rgba(255,255,255,0) 100%)',
          }}
        />
        {/* Subtle bottom fade so the hero blends into the next section */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.95) 100%)' }}
        />
        {/* Premium teal halo — kept low so it doesn't tint the advisor's face */}
        <div
          aria-hidden
          className="absolute top-[-15%] right-[-10%] rounded-full pointer-events-none"
          style={{
            width: 720, height: 720,
            background: 'radial-gradient(circle, rgba(31,167,161,0.10) 0%, transparent 70%)',
            filter: 'blur(90px)',
          }}
        />
        <div
          aria-hidden
          className="absolute bottom-[-20%] left-[-10%] rounded-full pointer-events-none"
          style={{
            width: 560, height: 560,
            background: 'radial-gradient(circle, rgba(30,91,168,0.08) 0%, transparent 70%)',
            filter: 'blur(100px)',
          }}
        />

        <div className="relative z-10 container mx-auto px-4 max-w-7xl pt-8 lg:pt-12 pb-14 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* ─── LEFT — Copy + features + CTAs ─────────────────────────── */}
            <div className="lg:col-span-6 flex flex-col gap-6">
              <div
                className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] vc-hero-in"
                style={{ background: '#DCEFEC', color: '#1A8F8A' }}
                data-testid="hero-eyebrow"
              >
                <Sparkles className="h-3 w-3" />
                Independent Healthcare Coordination in Vietnam
              </div>

              <h1
                className="font-extrabold tracking-tight text-slate-900 leading-[1.08] vc-hero-in-1"
                style={{ fontSize: 'clamp(2rem, 3.6vw, 2.6rem)', letterSpacing: '-0.02em' }}
                data-testid="hero-title"
              >
                Healthcare coordination
                <span className="block text-slate-900">in Vietnam</span>
              </h1>

              <p
                className="text-slate-500 text-base lg:text-lg leading-relaxed max-w-xl vc-hero-in-2"
                data-testid="hero-subtitle"
              >
                VietClinics is an independent coordination service for international
                patients, expats and travellers. We help you explore treatments and
                coordinate non-emergency care with selected providers — who make all
                diagnoses, decisions and final prices.
              </p>

              {/* Trust feature row — 4 premium pills */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-6 max-w-xl pt-2 vc-hero-in-3">
                {TRUST_FEATURES.map(({ icon: Icon, title, sub }, idx) => (
                  <div key={title} className="flex items-start gap-3" data-testid={`hero-trust-${idx + 1}`}>
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, #DCEFEC 0%, #F0FAF8 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(31,167,161,0.18)',
                      }}
                    >
                      <Icon className="h-[18px] w-[18px] text-[#1A8F8A]" />
                    </div>
                    <div className="leading-snug pt-0.5">
                      <p className="text-sm font-extrabold text-slate-900 tracking-tight">{title}</p>
                      <p className="text-xs text-slate-500 leading-snug mt-0.5">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-1 vc-hero-in-4">
                <a
                  href="#request-appointment"
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-7 py-3.5 text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(31,167,161,0.32)]"
                  style={{
                    background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                    boxShadow: '0 14px 30px rgba(31,167,161,0.28)',
                  }}
                  data-testid="hero-request-appointment-btn"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Request Care
                </a>
                <Link
                  to={buildLocalizedPath('/treatments', language)}
                  className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-2xl px-7 py-3.5 text-[15px] border-2 border-[#1FA7A1] hover:bg-[#1FA7A1]/5 hover:-translate-y-0.5 transition-all"
                  data-testid="hero-explore-treatments-btn"
                >
                  Explore Treatments
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={buildLocalizedPath('/urgent-medical-care', language)}
                  className="inline-flex items-center gap-2 bg-white text-slate-700 font-bold rounded-2xl px-7 py-3.5 text-[15px] border-2 border-slate-200 hover:border-slate-300 hover:-translate-y-0.5 transition-all"
                  data-testid="hero-urgent-care-btn"
                >
                  Urgent Care
                </Link>
              </div>
            </div>

            {/* ─── RIGHT — Spacer column carrying floating cards (image is the
                section background; this column reserves room and anchors the
                world-class card + phone mockup at the right side of the hero) ── */}
            <div
              className="lg:col-span-6 relative min-h-[420px] lg:min-h-[560px]"
              data-testid="hero-visual"
            >
              {/* ── Floating "World-Class Experience" card (bottom-LEFT) ─── */}
              <div
                className="hidden md:block absolute -bottom-20 -left-3 lg:-bottom-28 lg:-left-8 max-w-[300px] rounded-[22px] p-5 lg:p-6 vc-float-slow"
                style={{
                  background: 'rgba(255,255,255,0.45)',
                  backdropFilter: 'blur(22px) saturate(1.2)',
                  WebkitBackdropFilter: 'blur(22px) saturate(1.2)',
                  border: '1px solid rgba(255,255,255,0.55)',
                  boxShadow: '0 24px 60px rgba(15,42,85,0.16)',
                }}
                data-testid="hero-trust-card"
              >
                <p className="text-[10px] font-extrabold text-[#1FA7A1] uppercase tracking-[0.18em] mb-2">
                  Healthcare in Vietnam
                </p>
                <h3 className="text-[15px] font-extrabold text-slate-900 leading-snug tracking-tight mb-4">
                  Your enquiry,<br />coordinated next steps
                </h3>
                <ul className="space-y-2.5">
                  {COORDINATION_BULLETS.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2 text-[12px] text-slate-700">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#1FA7A1] flex-shrink-0 mt-[2px]" />
                      <span className="leading-snug">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ── Floating phone mockup (bottom-RIGHT, opposite the card) ──
                  If the supplied asset is a pre-composed phone (frame + screen
                  in one image), render it bare with a soft shadow — no extra
                  bezel wrapper to avoid a double-frame effect. Otherwise the
                  CSS iPhone shell renders with a teal placeholder screen. */}
              {phoneMockupUrl ? (
                <img
                  src={phoneMockupUrl}
                  alt="VietClinics mobile app"
                  loading="lazy"
                  className="hidden sm:block absolute -bottom-24 -right-3 lg:-bottom-36 lg:-right-10 w-[160px] lg:w-[200px] vc-float-fast"
                  style={{
                    filter: 'drop-shadow(0 24px 50px rgba(15,42,85,0.28))',
                  }}
                  data-testid="hero-phone-mockup"
                />
              ) : (
                <div
                  className="hidden sm:block absolute -bottom-10 -right-3 lg:-right-8 w-[110px] lg:w-[130px] aspect-[9/19] rounded-[24px] overflow-hidden vc-float-fast"
                  style={{
                    background: 'linear-gradient(180deg, #0f2b55 0%, #1a2540 100%)',
                    boxShadow: '0 24px 50px rgba(15,42,85,0.28), 0 0 0 6px #0f1a30, 0 0 0 7px rgba(255,255,255,0.10)',
                  }}
                  data-testid="hero-phone-mockup"
                >
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-3 bg-[#0f1a30] rounded-b-xl z-10" />
                  {/* Placeholder screen — premium teal gradient with hint */}
                  <div
                    className="absolute inset-[2px] rounded-[20px] flex flex-col items-center justify-center text-center px-2"
                    style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #DCEFEC 100%)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-2xl flex items-center justify-center mb-2"
                      style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}
                    >
                      <Stethoscope className="h-4 w-4 text-white" />
                    </div>
                    <p className="text-[9px] font-extrabold text-[#1FA7A1] uppercase tracking-[0.15em]">
                      App Soon
                    </p>
                    <p className="text-[8px] text-slate-500 mt-1 leading-snug">
                      Concierge on&nbsp;the&nbsp;go
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── 1b. SELECTED HEALTHCARE PARTNERS (compact trust strip) ────────── */}
      <section
        className="relative py-6 lg:py-7 border-b border-slate-100"
        style={{ background: 'linear-gradient(180deg, #F6FBFA 0%, #FFFFFF 100%)' }}
        data-testid="home-partners-section"
      >
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2
            className="text-sm font-bold uppercase tracking-wider text-[#1A8F8A]"
            data-testid="partners-heading"
          >
            Selected Healthcare Partners
          </h2>
          <p
            className="text-xs lg:text-sm text-slate-500 leading-relaxed max-w-2xl mx-auto mt-2"
            data-testid="partners-copy"
          >
            VietClinics coordinates care with selected licensed providers, including partners with
            JCI/AACI accreditations and certifications to relevant ISO standards. These credentials
            apply to individual providers and vary by facility, location and scope; VietClinics is an
            independent coordination service.{' '}
            <Link
              to={buildLocalizedPath('/how-it-works', language)}
              className="text-[#1FA7A1] font-semibold hover:underline whitespace-nowrap"
              data-testid="partners-how-we-work-link"
            >
              How we work
            </Link>
          </p>
        </div>
      </section>

      {/* ── 2. URGENT MEDICAL CARE (moved above treatments) ───────────────── */}
      <UrgentCareCitiesSection />

      {/* ── 3. POPULAR TREATMENTS IN VIETNAM ──────────────────────────────── */}
      <PopularTreatments />

      {/* ── 3b. BROWSE BY CATEGORY (entry to /{category}-…-in-vietnam) ─────── */}
      <CategoryDestinationsSection />

      {/* ── 4. HOW VIETCLINICS WORKS (5 steps) ────────────────────────────── */}
      <HowVietClinicsWorks />

      {/* ── 5. TREATMENT GUIDES & RESOURCES ───────────────────────────────── */}
      <TreatmentGuides />

      {/* ── 6. REQUEST APPOINTMENT FORM ───────────────────────────────────── */}
      <section id="request-appointment" className="py-20 bg-white" data-testid="request-appointment-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <RequestAppointmentForm testIdPrefix="home-req" />
        </div>
      </section>

      {/* ── 7. CTA FOOTER ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-white" data-testid="conversion-cta-section">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div
            className="rounded-[28px] px-8 py-12 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%)' }}
            />
            <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest mb-3 relative">{t('cta.eyebrow')}</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 tracking-tight relative">
              {t('cta.title')}
            </h2>
            <p className="text-white/80 text-sm mb-8 max-w-xl mx-auto leading-relaxed relative">
              {t('cta.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center relative">
              <a
                href="#request-appointment"
                className="bg-white text-[#1FA7A1] font-bold rounded-full px-8 py-3.5 flex items-center gap-2 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                data-testid="cta-request-appointment-btn"
              >
                {t('common.requestTreatment')} <ArrowRight className="h-4 w-4" />
              </a>
              <Link to={buildLocalizedPath('/treatments', language)}>
                <button
                  className="text-white font-semibold rounded-full px-8 py-3.5 border border-white/30 hover:bg-white/15 transition-all duration-200 backdrop-blur-sm"
                  data-testid="cta-explore-treatments-btn"
                >
                  {t('common.exploreTreatments')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingAppPanel />
    </div>
  );
};

export default Home;
